"use client";

import { use, useEffect, useRef, useState } from "react";
import CommonSchema from "@/components/common/CommonSchema";
import { motion } from "motion/react";
import {
  ChevronLeft,
  Calendar,
  User,
  Clock,
  Share2,
  ChevronRight,
  ChevronDown,
} from "lucide-react";
import Link from "next/link";
import Image from "next/image";
import { FadeInSection } from "@/components/common/FadeInSection";
import { IBlog } from "@/slices/blogSlice";
import {
  calculateReadingTime,
  cleanHeaderTags,
  createLookupObject,
} from "@/_helper/helper";
import { ICMS } from "@/slices/homeSlice";
import { Banner } from "../common/Banner";

export default function BlogDetailClient({
  blogData,
  allBlogs,
  CMSList,
}: {
  blogData: IBlog;
  allBlogs: IBlog[];
  CMSList: ICMS[];
}) {
  const [lookUpCMSData, setLookUpCMSData] = useState<Record<string, ICMS>>({});
  const [openFAQIndex, setOpenFAQIndex] = useState<number | null>(null);

  useEffect(() => {
    if (CMSList && CMSList.length > 0) {
      setLookUpCMSData(createLookupObject(CMSList, "Name"));
    }
  }, [CMSList]);

  const activeFAQs =
    blogData?.BlogFAQList?.filter((faq: any) => faq.IsActive)?.sort(
      (a: any, b: any) => (a.SequenceNo ?? 0) - (b.SequenceNo ?? 0),
    ) || [];

  return (
    <div className="min-h-screen bg-[#fdfcfa]">
      <CommonSchema />
      <Banner
        title={""}
        description={""}
        bgImage={blogData?.BlogBannerImage}
        backLinkText="Back to Blogs"
        backLinkUrl="/blogs"
      />
      {/* Hero Image */}
      <section className="pt-12">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            {/* <Image
              src={blogData.BlogBannerImage}
              alt={blogData.BlogTitle}
              fill
              className="object-cover"
              priority
            /> */}
            {/* <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" /> */}

            {/* <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12"> */}
            {/* <div className="max-w-4xl mx-auto"> */}
            {/* <Link
                  href="/blog"
                  className="inline-flex items-center gap-2 text-white/90 mb-4 hover:gap-3 transition-all"
                >
                  <ChevronLeft className="w-5 h-5" />
                  <span style={{ fontFamily: "Jost, sans-serif" }}>
                    Back to Blog
                  </span>
                </Link> */}

            {/* <div className="mb-4">
                  <span
                    className="inline-block bg-[#d4af37] text-[#2a2520] px-4 py-1 text-sm font-semibold"
                    style={{ fontFamily: "Jost, sans-serif" }}
                  >
                    {blogData.Category.name}
                  </span>
                </div> */}

            <h1
              className="text-4xl md:text-5xl lg:text-6xl mb-6 leading-tight"
              style={{
                fontFamily: "Cormorant Garamond, serif",
                fontWeight: 600,
              }}
            >
              {blogData.BlogTitle || "Blog Post"}
            </h1>

            <div
              className="flex flex-wrap items-center gap-6"
              style={{ fontFamily: "Jost, sans-serif" }}
            >
              {/* <div className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    {blogData.AuthorName}
                  </div> */}
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                {blogData.BlogDate}
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4" />
                {calculateReadingTime(blogData.LongDescription || "")}
              </div>
            </div>
            {/* </div> */}
            {/* </div> */}
          </motion.div>
        </div>
      </section>

      {/* Article Content */}
      <article className="py-16">
        <div className="max-w-7xl mx-auto px-6">
          <FadeInSection>
            <div
              className="prose prose-lg max-w-none blog-content"
              style={{ fontFamily: "Jost, sans-serif", fontWeight: 300 }}
              dangerouslySetInnerHTML={{
                __html: cleanHeaderTags(blogData.LongDescription),
              }}
            />
          </FadeInSection>

          {/* Blog FAQs */}
          {activeFAQs.length > 0 && (
            <div className="mt-16 pt-12 border-t-2 border-[#5a6f3e]/10">
              <FadeInSection>
                <h2
                  className="text-3xl md:text-4xl text-[#2a2520] mb-8"
                  style={{
                    fontFamily: "Cormorant Garamond, serif",
                    fontWeight: 600,
                  }}
                >
                  FAQ
                </h2>
              </FadeInSection>

              <div className="space-y-4">
                {activeFAQs.map((faq: any, index: number) => (
                  <FadeInSection key={faq.ID || index} delay={index * 0.05}>
                    <motion.div
                      className="bg-white shadow-md border border-[#5a6f3e]/10 overflow-hidden"
                      whileHover={{
                        boxShadow: "0 8px 24px rgba(90, 111, 62, 0.08)",
                      }}
                    >
                      <button
                        onClick={() =>
                          setOpenFAQIndex(openFAQIndex === index ? null : index)
                        }
                        aria-expanded={openFAQIndex === index}
                        className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-[#5a6f3e]/5 transition-colors cursor-pointer"
                      >
                        <span
                          className="text-sm md:text-lg text-[#2a2520] pr-4"
                          style={{
                            fontFamily: "Jost, sans-serif",
                            fontWeight: 500,
                          }}
                        >
                          {faq.Question}
                        </span>
                        <motion.div
                          animate={{ rotate: openFAQIndex === index ? 180 : 0 }}
                          transition={{ duration: 0.3 }}
                        >
                          <ChevronDown className="w-5 h-5 text-[#5a6f3e] flex-shrink-0" />
                        </motion.div>
                      </button>

                      <motion.div
                        initial={false}
                        animate={{
                          height: openFAQIndex === index ? "auto" : 0,
                          opacity: openFAQIndex === index ? 1 : 0,
                        }}
                        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                        className="overflow-hidden"
                      >
                        <div className="px-6 pb-5 pt-1">
                          <p
                            className="text-[#2a2520]/70 leading-relaxed text-sm md:text-lg"
                            style={{
                              fontFamily: "Jost, sans-serif",
                              fontWeight: 300,
                            }}
                          >
                            {faq.Answer}
                          </p>
                        </div>
                      </motion.div>
                    </motion.div>
                  </FadeInSection>
                ))}
              </div>
            </div>
          )}

          {/* Share Section */}
          {/* <FadeInSection delay={0.2}>
            <div className="mt-12 pt-8 border-t-2 border-[#5a6f3e]/10">
              <div className="flex items-center gap-4">
                <span
                  className="text-[#2a2520]/70"
                  style={{ fontFamily: "Jost, sans-serif", fontWeight: 600 }}
                >
                  Share this article:
                </span>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  className="bg-[#5a6f3e]/10 text-[#5a6f3e] p-3 hover:bg-[#5a6f3e] hover:text-white transition-colors"
                >
                  <Share2 className="w-5 h-5" />
                </motion.button>
              </div>
            </div>
          </FadeInSection> */}
        </div>
      </article>

      {/* CTA Section */}
      {lookUpCMSData["Blogs - Section 1 : CTA"] && (
        <section className="py-16 bg-gradient-to-br from-[#5a6f3e] to-[#4a5f2e] text-white">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <FadeInSection>
              <div
                dangerouslySetInnerHTML={{
                  __html: cleanHeaderTags(
                    lookUpCMSData["Blogs - Section 1 : CTA"].Template || "",
                  ),
                }}
              ></div>
              {/* <h2
              className="text-4xl md:text-5xl mb-6 leading-tight"
              style={{
                fontFamily: "Cormorant Garamond, serif",
                fontWeight: 600,
              }}
            >
              Plan Your Stay at Sarasiruham Resort Today
            </h2>
            <p
              className="text-xl text-white/90 mb-10"
              style={{ fontFamily: "Jost, sans-serif", fontWeight: 300 }}
            >
              Experience the luxury and serenity described in this article
            </p> */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.div whileHover={{ scale: 1.05 }}>
                  <Link
                    href="/rooms"
                    className="h-full bg-[#d4af37] text-[#2a2520] px-10 py-4 shadow-xl hover:bg-[#c9a961] transition-colors text-lg inline-block"
                    style={{ fontFamily: "Jost, sans-serif", fontWeight: 600 }}
                  >
                    Book Your Room
                  </Link>
                </motion.div>
                <motion.div whileHover={{ scale: 1.05 }}>
                  <Link
                    href="/contact"
                    className="border-2 border-white text-white px-10 py-4 hover:bg-white hover:text-[#5a6f3e] transition-all text-lg inline-block"
                    style={{ fontFamily: "Jost, sans-serif", fontWeight: 600 }}
                  >
                    Contact Us
                  </Link>
                </motion.div>
              </div>
            </FadeInSection>
          </div>
        </section>
      )}

      {/* Related Blogs */}
      <section className="py-20 bg-[#fdfcfa] mb-20">
        <div className="max-w-7xl mx-auto px-6">
          <FadeInSection>
            <h2
              className="text-4xl md:text-5xl text-[#2a2520] mb-12 text-center"
              style={{
                fontFamily: "Cormorant Garamond, serif",
                fontWeight: 600,
              }}
            >
              Related Articles
            </h2>
          </FadeInSection>

          <div className="grid md:grid-cols-3 gap-8">
            {allBlogs
              ?.filter((related: any) => related.ID !== blogData.ID)
              ?.slice(0, 3)
              ?.map((related: any, index: number) => (
                <FadeInSection key={related.ID} delay={index * 0.1}>
                  <Link href={`/blogs/${related.BlogURL}`}>
                    <motion.div
                      whileHover={{ y: -8 }}
                      className="bg-white shadow-xl overflow-hidden group h-full"
                    >
                      <div className="relative h-48 overflow-hidden">
                        <Image
                          src={related.Image}
                          alt={related.Title}
                          fill
                          className="object-cover group-hover:scale-110 transition-transform duration-700"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                        {/* <div className="absolute top-4 left-4">
                        <span
                          className="bg-[#d4af37] text-[#2a2520] px-3 py-1 text-xs font-semibold"
                          style={{ fontFamily: "Jost, sans-serif" }}
                        >
                          {related.category}
                        </span>
                      </div> */}
                      </div>
                      <div className="p-6">
                        <h3
                          className="text-xl text-[#2a2520] mb-3 group-hover:text-[#5a6f3e] transition-colors leading-tight"
                          style={{
                            fontFamily: "Cormorant Garamond, serif",
                            fontWeight: 600,
                          }}
                        >
                          {related.Title}
                        </h3>
                        <div
                          className="flex items-center gap-2 text-[#5a6f3e] font-semibold"
                          style={{ fontFamily: "Jost, sans-serif" }}
                        >
                          Read Article
                          <span className="sr-only">about {related.Title}</span>
                          <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </div>
                      </div>
                    </motion.div>
                  </Link>
                </FadeInSection>
              ))}
          </div>
        </div>
      </section>

      {/* Custom Styles for Blog Content */}
      <style>{`
        .custom-blog-content h2 {
          font-family: 'Cormorant Garamond', serif;
          font-weight: 600;
          font-size: 2.25rem;
          color: #2a2520;
          margin-top: 2.5rem;
          margin-bottom: 1.25rem;
          line-height: 1.2;
        }
        .custom-blog-content h3 {
          font-family: 'Cormorant Garamond', serif;
          font-weight: 600;
          font-size: 1.75rem;
          color: #5a6f3e;
          margin-top: 2rem;
          margin-bottom: 1rem;
          line-height: 1.3;
        }
        .custom-blog-content p {
          color: rgba(42, 37, 32, 0.85);
          line-height: 1.8;
          margin-bottom: 1.75rem;
          font-size: 1.125rem;
        }
        .custom-blog-content ul {
          margin: 1.75rem 0;
          padding-left: 1.5rem;
          list-style-type: disc;
        }
        .custom-blog-content li {
          color: rgba(42, 37, 32, 0.85);
          margin-bottom: 0.875rem;
          line-height: 1.7;
          font-size: 1.125rem;
        }
        .custom-blog-content a {
          color: #5a6f3e;
          text-decoration: underline;
          text-underline-offset: 4px;
          font-weight: 500;
          transition: color 0.3s;
        }
        .custom-blog-content a:hover {
          color: #d4af37;
        }
        .custom-blog-content strong {
          color: #2a2520;
          font-weight: 600;
        }
      `}</style>
    </div>
  );
}
